import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactFlow, { addEdge, Background, useReactFlow } from "reactflow";
import "reactflow/dist/style.css";
import "../nodes/ai-node.css";
import AiNode from "../nodes/AiNode";
import "../nodes/automation-trigger-node.css";
import "../nodes/button-node.css";
import ButtonNode from "../nodes/ButtonNode";
import "../nodes/condition-node.css";
import ConditionNode from "../nodes/ConditionNode";
import "../nodes/consent-node.css";
import ConsentNode from "../nodes/ConsentNode";
import "../nodes/custom-edge.css";
import CustomEdge from "../nodes/CustomEdge";
import "../nodes/message-node.css";
import MessageNode from "../nodes/MessageNode";
import "../nodes/note-node.css";
import Sidebar from "../nodes/Sidebar";
import "../nodes/sidebar.css";
import "../nodes/tag-node.css";
import TagNode from "../nodes/TagNode";
import "../nodes/template-node.css";
import TemplateNode from "../nodes/TemplateNode";
import "../nodes/trigger-node.css";
import TriggerNode from "../nodes/TriggerNode";
import "../nodes/waiting-node.css";
import WaitingNode from "../nodes/WaitingNode";
import "../nodes/webhook-node.css";
import WebhookNode from "../nodes/webhookNode";

import { useFindMany, useGlobalAction } from "@gadgetinc/react";
import { api } from "../api";
import { WhatsappTemplateCategoryEnum } from "../enums";
import { deleteEdge } from "../functions/deleteEdge";
import AutomationTriggerNode from "../nodes/AutomationTriggerNode";
import NoteNode from "../nodes/NoteNode";
import { CustomerContext } from "../providers";
import {
  onDropNodeCallback,
  onEdgesDeleteCallback,
  onNodesDeleteCallback,
  Panel,
} from "./flow_editor";
import AutocompleteInput from "./tailwind/autocomplete-input";
import { message } from "antd";

const nodeTypes = {
  trigger: TriggerNode,
  message: MessageNode,
  button: ButtonNode,
  consent: ConsentNode,
  condition: ConditionNode,
  webhook: WebhookNode,
  wait: WaitingNode,
  tag: TagNode,
  template: TemplateNode,
  ai: AiNode,
  automationTrigger: AutomationTriggerNode,
  note: NoteNode,
};
const edgeTypes = { customedge: CustomEdge };

const proOptions = {
  account: "paid-pro",
  hideAttribution: true,
};

export default function FlowEditor({
  nodes,
  setNodes,
  onNodesChange,
  edges,
  setEdges,
  onEdgesChange,
  selectedNodeId,
  setSelectedNodeId,
  modifications,
  setModifications,
  scenario,
  campaignId,
  isLoading,
  setIsLoading,
  isSubmitLoading,
  setIsSubmitLoading,
}) {
  const { user, integrations } = useContext(CustomerContext);

  useEffect(() => {
    let hide;
    if (isLoading || isSubmitLoading) {
      hide = message.loading(
        `Wait while ${isSubmitLoading ? "submitting" : "saving"}...`,
        0,
      );
    }
    return () => {
      if (hide) {
        hide();
      }
    };
  }, [isLoading, isSubmitLoading]);

  const [result, getWhatsAppTemplates] = useGlobalAction(
    api.getWhatsAppTemplates,
    {
      limit: "5000",
    },
  );
  const { data: templateData360 } = result;
  useEffect(() => {
    console.log("fetching templates start");
    getWhatsAppTemplates({
      limit: "5000",
    });
    console.log("fetching templates end");
  }, [isLoading]);

  const templates = templateData360?.waba_templates ?? templateData360;
  if (templateData360?.waba_templates) {
    templates?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  const templatesByName = useMemo(() => {
    if (templates?.length) {
      return templates.reduce((acc, template) => {
        acc[template.name] = template;
        return acc;
      }, {});
    }
    return {};
  }, [templates]);

  useEffect(() => {
    if (templates && nodes) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.type === "template") {
            const selectedTemplate = Object.values(templates).find(
              (template) => template.name === node.data.whatsappTemplateName,
            );
            if (node.data.limitedTimeOffer) {
              node.data.category =
                WhatsappTemplateCategoryEnum.LIMITED_TIME_OFFER;
            } else if (node.data.cards) {
              node.data.category = WhatsappTemplateCategoryEnum.CAROUSEL;
            } else {
              node.data.category =
                selectedTemplate?.category ||
                node.data.category ||
                WhatsappTemplateCategoryEnum.UTILITY;
            }
            if (
              !selectedTemplate &&
              node.data.whatsappTemplateName !== "" &&
              !node.data.isEditing &&
              !node.data.isPending &&
              !isLoading
            ) {
              node.data.hasFailed = true;
            } else if (
              (node.data.whatsappTemplateName !== "" ||
                node.data.whatsappTemplateName !== null) &&
              selectedTemplate &&
              selectedTemplate.status.toLowerCase() === "rejected" &&
              !isLoading
            ) {
              node.data.hasFailed = true;
              node.data.isPending = false;
            } else if (selectedTemplate) {
              node.data.hasFailed = false;
              node.data.isPending = false;
            } else {
              // If template is just submitted
              node.data.hasFailed = false;
            }
            if (
              ["submitted", "pending"].includes(
                selectedTemplate?.status.toLowerCase(),
              ) ||
              node.data.isPending
            ) {
              node.data.isPending = true;
            } else {
              node.data.isPending = false;
            }
            node.data.status = selectedTemplate?.status.toLowerCase();
          }
          return node;
        }),
      );
    }
  }, [templates]);

  // this hook handles the computation of the layout once the elements or the direction changes
  const { deleteElements } = useReactFlow();

  // For drag and drop
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Edition Panel
  const [isPanelOpened, setIsPanelOpened] = useState(false);

  const handleMessageChange = useCallback((field, newValue, nodes) => {
    const selectedNode = nodes.find((n) => n.selected);

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          // it's important that you create a new object here
          // in order to notify react flow about the change
          node.data = {
            ...node.data,
            [field]: newValue,
          };
        }
        return node;
      }),
    );
    setModifications(true);
  }, []);

  const selectedNode = useMemo(
    () => nodes.find((n) => n?.id === selectedNodeId),
    [selectedNodeId, nodes],
  );

  let id = nodes.length + 1;
  const getId = () => `${id++}`;

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    onDropNodeCallback({
      reactFlowWrapper,
      reactFlowInstance,
      setNodes,
      setSelectedNodeId,
      getId,
      setModifications,
      setIsPanelOpened,
    }),
    [reactFlowInstance],
  );

  const onNodesDelete = useCallback(
    onNodesDeleteCallback({
      setIsPanelOpened,
      api,
    }),
  );

  const onEdgesDelete = useCallback(onEdgesDeleteCallback({ edges, api }));

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge(params, eds));
      setModifications(true);
    },
    [setEdges],
  );

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Perform actions before the component unloads
      event.preventDefault();
      event.returnValue = "";
    };
    if (modifications) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [modifications]);

  const [{ data }] = useFindMany(api.tag, {
    first: 250,
    select: {
      id: true,
      name: true,
      createdAt: true,
      updatedAt: true,
    },
  });

  const deselectedTags = useMemo(() => {
    if (!data) return [];

    return data
      .map((t) => {
        return {
          value: t.name?.toLowerCase(),
          label: t.name,
          id: t.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const [selectedConditionTags, setSelectedConditionTags] = useState([]);
  const [inputTagConditionValue, setInputTagConditionValue] = useState("");
  const [conditionTags, setConditionTags] = useState([]);

  // Not very clean. The goal is to update tags without creating an infinite loop
  useEffect(() => {
    setConditionTags(deselectedTags);
  }, [deselectedTags]);

  useEffect(() => {
    setInputTagConditionValue(selectedNode?.data?.valueTag?.name ?? "");
  }, [selectedNode]);

  const updateTagConditionInput = (e) => {
    let value;
    if (e) {
      value = typeof e === "string" ? e : e.target.value;
    } else {
      value = "";
    }
    setInputTagConditionValue(value);

    setTimeout(() => {
      if (value === "") {
        setConditionTags(deselectedTags);
        return;
      }

      const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const filterRegex = new RegExp(`^${escapedValue}`, "i");
      const resultOptions = deselectedTags?.filter((option) =>
        option.label.match(filterRegex),
      );
      setConditionTags(resultOptions);
    }, 300);
  };

  const updateTagConditionSelection = async (e, nodes) => {
    const selectedConditionTag = deselectedTags.find(
      (c) => c.value.toLowerCase() === e.value,
    );
    const selectedText = selectedConditionTag.label;
    const selectedName = selectedConditionTag.value;
    const selectedId = selectedConditionTag.id;
    setSelectedConditionTags([selectedConditionTag]);
    setInputTagConditionValue(selectedText);
    const valueTag = {
      id: selectedId,
      name: selectedName,
    };
    handleMessageChange("valueTag", valueTag, nodes, setNodes, selectedNode);
    handleMessageChange("trueCount", null, nodes, setNodes, selectedNode);
    handleMessageChange("falseCount", null, nodes, setNodes, selectedNode);
  };

  const [isUploading, setIsUploading] = useState(false);
  const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);

  useEffect(() => {
    if (selectedConditionTags.length > 0) {
      const selectedConditionTag = selectedConditionTags[0];
      setInputTagConditionValue(selectedConditionTag.label);
    }
  }, [selectedConditionTags]);

  // Panel design
  const TagAutocompleteCondition = (
    <>
      <AutocompleteInput
        value={inputTagConditionValue}
        onChange={updateTagConditionInput}
        options={conditionTags}
        selected={selectedConditionTags}
        onSelect={(e) => {
          updateTagConditionSelection(e, nodes);
        }}
      />
    </>
  );

  const edgesWithDelete = edges.map((edge) => ({
    ...edge,
    type: "customedge",
    data: {
      ...edge.data,
      onDelete: edge.data?.locked
        ? undefined
        : () => deleteEdge(edge, setEdges, api),
    },
  }));

  return (
    <div className="flex relative">
      <div className="absolute w-4/5 left-1/2 bottom-2 transform -translate-x-1/2 z-10">
        <Sidebar />
      </div>
      {(isLoading || isSubmitLoading) && <div className="screen-overlay" />}

      <div className={`flex-1 h-[calc(100vh-92px)] `} ref={reactFlowWrapper}>
        <ReactFlow
          fitView
          proOptions={proOptions}
          minZoom={0.1}
          maxZoom={1}
          nodes={nodes}
          edges={edgesWithDelete}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          elementsSelectable={true}
          onNodesDelete={onNodesDelete}
          onEdgesDelete={onEdgesDelete}
          onNodeClick={() => setIsPanelOpened(true)}
          onPaneClick={() => setIsPanelOpened(false)}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onInit={setReactFlowInstance}
          deleteKeyCode={null}
        >
          <Background color="#aaa" gap={20} />
        </ReactFlow>
      </div>

      <Panel
        api={api}
        selectedNode={selectedNode}
        nodes={nodes}
        setNodes={setNodes}
        edges={edges}
        setEdges={setEdges}
        TagAutocompleteCondition={TagAutocompleteCondition}
        user={user}
        isUploading={isUploading}
        setIsUploading={setIsUploading}
        isEmojiPickerVisible={isEmojiPickerVisible}
        setIsEmojiPickerVisible={setIsEmojiPickerVisible}
        templates={templates}
        templatesByName={templatesByName}
        isPanelOpened={isPanelOpened}
        setIsPanelOpened={setIsPanelOpened}
        deleteElements={deleteElements}
        integrations={integrations}
        scenario={scenario}
        setModifications={setModifications}
        campaignId={campaignId}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        isSubmitLoading={isSubmitLoading}
        setIsSubmitLoading={setIsSubmitLoading}
      />
    </div>
  );
}
